import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { useTopRisks } from "@/hooks/useTopRisks";
import { RISK_SEVERITY_TONE } from "@/lib/badgeTones";
import { RisksHelpPopover } from "@/components/risks/RisksHelpPopover";

export function TopRisksWidget(): React.ReactElement {
  const { t } = useTranslation();
  const { risks, isLoading, isError, error } = useTopRisks();

  return (
    <Card data-testid="widget-top-risks">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-1">
          <CardTitle className="text-sm font-medium">{t("home.widgets.topRisks.title")}</CardTitle>
          <RisksHelpPopover iconSize="sm" />
        </div>
        <span className="text-2xl font-semibold tabular-nums">{risks.length}</span>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
        ) : isError ? (
          <ApiErrorNotice
            error={error}
            fallbackMessage={t("common.apiError.fallback")}
            className="text-destructive text-sm"
          />
        ) : risks.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("home.widgets.topRisks.empty")}</p>
        ) : (
          <ul className="space-y-2">
            {risks.map((risk) => {
              const spec = RISK_SEVERITY_TONE[risk.severity];
              return (
                <li
                  key={risk.id ?? `${risk.risk_type}-${risk.detected_at}`}
                  className="border-border flex items-start gap-2 border-b pb-2 last:border-0"
                >
                  <Badge tone={spec.tone} emphasis={spec.emphasis}>
                    {t(`home.widgets.topRisks.severity.${risk.severity}`)}
                  </Badge>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {risk.description || risk.risk_type}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {t(`home.widgets.topRisks.type.${risk.risk_type}`)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
